import { useCallback } from 'react';
import type { SelectName } from '../utils';
import { BuildVehicleSelects, arraysMatch } from '../utils';
import type { ComboBoxOptions } from '@interstate/components/ComboBox';

const ALL = 'All';

const defaultToAllIfNoValue = (value: string[] | undefined): string[] => {
    return Array.isArray(value) && value.length > 0 && value[0] !== ALL ? value : [ALL];
};

export const useVehicleSelectChange = ({
    setInventoryTypes,
    setYears,
    setMakes,
    setModels,
    setTrims
}: {
    setInventoryTypes: (value: string[]) => void;
    setYears: (value: string[]) => void;
    setMakes: (value: string[]) => void;
    setModels: (value: string[]) => void;
    setTrims: (value: string[]) => void;
}) => {
    return useCallback(
        (selectName: SelectName, value: any, options?: ComboBoxOptions) => {
            const updateSelectAndClearSubsequent = (updatedValue: string[], targetSelectName: SelectName) => {
                const processedValue = defaultToAllIfNoValue(updatedValue);

                switch (targetSelectName) {
                    case BuildVehicleSelects.inventoryTypes:
                        setInventoryTypes(processedValue);
                        break;
                    case BuildVehicleSelects.years:
                        setYears(processedValue);
                        if (processedValue.includes(ALL)) {
                            setModels([ALL]);
                            setTrims([ALL]);
                        }
                        break;
                    case BuildVehicleSelects.makes:
                        setMakes(processedValue);
                        setModels([ALL]);
                        setTrims([ALL]);
                        break;
                    case BuildVehicleSelects.models:
                        setModels(processedValue);
                        setTrims([ALL]);
                        break;
                    case BuildVehicleSelects.trims:
                        setTrims(processedValue);
                        break;
                }
            };

            let normalizedValue = value;
            if (Array.isArray(value) && options) {
                const allOptions = options.filter((opt) => opt.value !== ALL).map((opt) => opt.value);

                if (arraysMatch(allOptions, value)) {
                    normalizedValue = [ALL];
                } else if (value.length === 2 && value[0] === ALL) {
                    normalizedValue = value.filter((val) => val !== ALL);
                } else if (value.includes(ALL)) {
                    normalizedValue = [ALL];
                }
            }

            const isMultiSelectField = [BuildVehicleSelects.makes, BuildVehicleSelects.models, BuildVehicleSelects.trims].includes(
                selectName
            );
            const hasMultipleValues = Array.isArray(normalizedValue) && normalizedValue.length > 1;
            const hasAllValue = Array.isArray(normalizedValue) ? normalizedValue.includes(ALL) : normalizedValue === ALL;

            if (selectName === BuildVehicleSelects.years && hasAllValue) {
                updateSelectAndClearSubsequent([ALL], selectName);
                return;
            }

            if (isMultiSelectField && (hasMultipleValues || hasAllValue)) {
                const finalValue = hasAllValue ? [ALL] : normalizedValue;
                updateSelectAndClearSubsequent(finalValue, selectName);
                return;
            }

            updateSelectAndClearSubsequent(normalizedValue, selectName);
        },
        [setInventoryTypes, setYears, setMakes, setModels, setTrims]
    );
};
